/* eslint-disable max-len */
import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { telemetry } from '../../@vendor/Posthog';
import { fuzzySearch, useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { Button, Colors, Input } from '../../ui';
import { useBreakpoints } from '../../ui/Breakpoints';
import { PopupTabs } from '../../ui/PopupTabs';
import { Star } from '../../ui/Star';
import { useResolve } from '../Container';
import { useDashboardVersion } from '../account/AccountHooks';
import { AccountModel } from '../account/AccountModel';
import { Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { useTemplateData, useTemplateStarred } from '../dashboard/templates/TemplateHooks';
import { SearchEmpty } from '../dashboard/templates/gallery/SearchEmpty';
import { StarredEmpty } from '../dashboard/templates/gallery/StarredEmpty';
import { TeamEmpty } from '../dashboard/templates/gallery/TeamEmpty';
import { TemplateActionMenu } from '../dashboard/templates/gallery/TemplateActionMenu';
import { TemplateCard } from '../dashboard/templates/gallery/TemplateCard';
import { TemplateWithStarData } from '../dashboard/templates/gallery/TemplateListView';
import { Wibble } from './Wibble';

const TemplateItem = React.memo<{ template: TemplateWithStarData, selected?: boolean, onClick: VoidCallback }>(({ template, selected, onClick }) => {

    const [starred, toggleStarred] = useTemplateStarred(template);

    return <TemplateCard.Wrapper>
        <TemplateCard clickable {...{ selected, onClick }}>
            <TemplateCard.Image>
                <TemplateCard.Thumb id={template.id} thumbUrl={template.thumbUrl} />
            </TemplateCard.Image>
            <TemplateCard.Info>
                <TemplateCard.Header>
                    <TemplateCard.Label title={template.label}>
                        {template.label}
                    </TemplateCard.Label>
                </TemplateCard.Header>
                <TemplateCard.Body>
                    <Copy>{template.description}</Copy>
                </TemplateCard.Body>
                <TemplateCard.Footer>
                    <TemplateCard.TagList>
                        {template.tags.map(x => <TemplateCard.Tag key={x.id} color={x.color}>{x.label}</TemplateCard.Tag>)}
                    </TemplateCard.TagList>
                </TemplateCard.Footer>
            </TemplateCard.Info>
        </TemplateCard>
        <TemplateCard.ActionMenu>
            <TemplateActionMenu readonly {...{ template }} />
        </TemplateCard.ActionMenu>
        <TemplateCard.StarButton active={starred}>
            <Star size="2" active={starred} onClick={toggleStarred} />
        </TemplateCard.StarButton>
    </TemplateCard.Wrapper>;
});

const InlineTemplateGalleryView = ({ initialTag, onSelect }: { initialTag?: string, onSelect(template: TemplateWithStarData): void }) => {

    const account = useObserver(useResolve(AccountModel));
    const version = useDashboardVersion();
    const { templates, tags } = useTemplateData(version);
    const [search, setSearch] = useState('');
    const [primary, setPrimary] = useState('all' as 'all' | 'team' | 'starred');
    const [secondary, setSecondary] = useState(initialTag || '');
    const selectedTag = useMemo(() => tags?.find(x => x.id === secondary || x.name === secondary), [tags, secondary]);
    const reset = (p: 'all' | 'team' | 'starred') => {
        setPrimary(p);
        setSecondary('');
        setSearch('');
    };

    useDebounce(() => search !== '' && telemetry.emit('template.search', { search }), 500);

    const primaryFilteredTemplates = useMemo(
        () => {
            const filtered = templates?.filter(x => {
                if (primary === 'team') return !!x.team;
                if (primary === 'starred') return x.starred;
                if (primary === 'all') return true;
                return true;
            });
            return (primary !== 'starred' && !!search.trim())
                ? fuzzySearch({
                    items: filtered,
                    keys: [{ name: 'searchableLabel', weight: 1 }, { name: 'description', weight: 1 }],
                    search,
                    threshold: 0.5,
                })
                : filtered;
        },
        [templates, primary, search]);

    const secondaryFilteredTemplates = useMemo(
        () => primaryFilteredTemplates?.filter(x => !secondary || !!x.tags.includes(selectedTag)),
        [primaryFilteredTemplates, secondary]);

    const relevantTags = useMemo(
        () => tags?.filter(x => x === selectedTag || primaryFilteredTemplates?.some(t => t.tags.includes(x))),
        [tags, primaryFilteredTemplates]);

    return <Flexer grow flex="column" align="center" gap="2">
        <Flexer flex="column" gap="2" maxWidth="105" width="100%">
            <PopupTabs border="none" spacing="1.5" size="lg">
                <PopupTabs.Item onClick={() => reset('all')} selected={primary === 'all'}>
                    All Templates
                </PopupTabs.Item>
                <PopupTabs.Item onClick={() => reset('team')} selected={primary === 'team'}>
                    {account.label} Templates
                </PopupTabs.Item>
                {version === 'next' &&
                    <PopupTabs.Item onClick={() => reset('starred')} selected={primary === 'starred'}>
                        Starred Templates
                    </PopupTabs.Item>
                }
            </PopupTabs>
            <Flexer flex="row" align="start" gap="2">
                <Flexer flex="row" align="center" gap=".75" wrap="wrap" grow>
                    {(primary === 'all') &&
                        <Flexer flex="row" gap=".75" align="center">
                            <Flexer width="16">
                                <Input autoFocus placeholder="Search templates" value={search} onChange={setSearch} />
                            </Flexer>
                            {!!relevantTags?.length &&
                                <Flexer margin="0 .25">
                                    <Wibble height="1.8" />
                                </Flexer>
                            }
                        </Flexer>
                    }
                    {!!relevantTags?.length &&
                        <Button onAction={() => setSecondary('')} variant={!selectedTag ? 'primary' : 'common'}>
                            Everything
                        </Button>
                    }
                    {relevantTags?.map(x =>
                        <Button key={x.id} onAction={() => setSecondary(x.id)} variant={x.id === selectedTag?.id ? 'primary' : 'common'}>
                            {x.label}
                        </Button>
                    )}
                </Flexer>
                {/* <Flexer flex="row" center shrink>
                <Copy bold flush size=".875">Sort by:</Copy>
                <SortModeSelector options={sortOptions} value={sort} onChange={setSort} />
            </Flexer> */}
            </Flexer>
        </Flexer>
        <Flexer relative grow flex="column" width="100%" maxWidth="105">
            {!!secondaryFilteredTemplates?.length &&
                <Flexer padding="0">
                    {secondaryFilteredTemplates &&
                        <Flexer flex="row" gap="2" wrap="wrap">
                            {secondaryFilteredTemplates?.map(x =>
                                <Flexer key={`${x.id}/${x.alias}`}>
                                    <TemplateItem template={x} onClick={() => onSelect(x)} />
                                </Flexer>
                            )}
                        </Flexer >
                    }
                </Flexer>
            }
            {primary === 'team' && !search && !primaryFilteredTemplates?.length && <TeamEmpty minHeight="32" />}
            {primary === 'starred' && !search && !primaryFilteredTemplates?.length && <StarredEmpty />}
            {!!search && !primaryFilteredTemplates?.length && <SearchEmpty />}
        </Flexer>
    </Flexer>;
};

const bpc = { lg: 104, md: 78, sm: 52, min: 0 };

export const NewBoardBuilderView = ({ initialTag, onComplete }: { initialTag: string, onComplete(template: TemplateWithStarData): void }) => {

    // 76rem
    const frame = useRef<HTMLDivElement>();
    const { size } = useBreakpoints(frame, bpc);

    const onSelectTemplate = useCallback((t: TemplateWithStarData) => {
        onComplete(t);
    }, [onComplete]);

    return <Flexer ref={frame} flex="column" grow>
        <Flexer flex="row" center height="5" borderBottom={`solid 1px ${Colors.border}`}>
            <Flexer width="100%" maxWidth={size} margin="0 1">
                <Title flush size="xl" color={Colors.text}>
                    Choose a template:
                </Title>
            </Flexer>
        </Flexer>
        <Flexer relative grow background="#F9FAFC">
            <Flexer inset="0" overflow="auto">
                <Flexer relative width={size || 'auto'} margin="2rem auto">
                    <Flexer margin="0 1">
                        <InlineTemplateGalleryView initialTag={initialTag} onSelect={onSelectTemplate} />
                    </Flexer>
                </Flexer>
            </Flexer>
        </Flexer>
    </Flexer>;
};
